import { AvailableOrderFulfillmentFieldsFragmentDoc } from '@domain/fulfillment-settings/blocs/available-order-fulfillment-fields-fragment.generated';
import { gql } from '@apollo/client';
import { NewOrder_OrderContextQueryVariables, OrderDomain } from '@/types';
import { UNPLACED_ORDER_STATUSES } from '@domain/fulfillment-settings/blocs/order-availability';
import { useAuth } from './auth-context';
import { useMemo } from 'react';

export const ORDER_FRAGMENT = gql`
  fragment NewOrder_OrderItemFields on orders_items {
    earliest_fulfillment_at
    notes
    order_item_prices {
      pack_quantity_at_order
    }
    restaurant_display_name
    variants_pack {
      item {
        description
        uuid
      }
      metadata
      uuid
    }
    uuid
  }

  fragment NewOrder_OrderFields on orders {
    additional_input_values
    fulfillment_timeslot_end
    fulfillment_timeslot_start
    fulfillment_type
    notes
    order_domain
    order_fulfillment_fees {
      amount_in_micros
      fulfillment_settings_name
      uuid
    }
    order_invoices {
      balance_in_micros
      due_at
      invoice_date
      invoice_number
      order_uuid
      uuid
    }
    orders_items {
      ...NewOrder_OrderItemFields
    }
    placed_at
    po_number
    restaurant_desired_delivery_time
    status
    storefront_metadata {
      name
      uuid
      visibility
    }
    supplier_accepted_delivery_time
    uuid
  }
`;

export const ORDER_CONTEXT_QUERY = gql`
  query NewOrder_OrderContext(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $unplacedOrderStatuses: [OrderStatus!]!
    $orderDomains: [OrderDomain!]!
    $employeeUUID: uuid
    $includeActiveOrder: Boolean!
  ) {
    businesses_by_pk(uuid: $supplierUUID) {
      address {
        timezone
        uuid
      }
      supplier_chats(where: { restaurant_uuid: { _eq: $restaurantUUID } }) {
        chats_employees(where: { employee_uuid: { _eq: $employeeUUID } })
          @include(if: $includeActiveOrder) {
          storefront_active_order_uuid
          uuid
        }
        custom_order_guide_editing_enabled
        custom_order_guide_sort_editing_enabled
        display_item_prices
        item_modifiers_enabled
        order_guide_merge_strategy
        po_number_display
        uuid
      }
      supplier_metadata {
        allow_delivery_date_edit
        display_item_code
        display_item_prices
        gtin_settings
        order_guide_merge_strategy
        po_number_display
        show_order_notes
        show_upc_scanner
        uuid
      }
      uuid
    }

    getAvailableOrderFulfillments(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      ...AvailableOrderFulfillmentFields
    }

    getOrders(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      filters: {
        statuses: $unplacedOrderStatuses
        order_domains: $orderDomains
      }
    ) {
      order {
        ...NewOrder_OrderFields
      }
    }
  }

  ${AvailableOrderFulfillmentFieldsFragmentDoc}
  ${ORDER_FRAGMENT}
`;

export const SET_ACTIVE_ORDER_MUTATION = gql`
  mutation NewOrder_SetActiveOrder($chatUUID: uuid!, $orderUUID: uuid) {
    setActiveOrder(chat_id: $chatUUID, order_id: $orderUUID) {
      chat_employee {
        storefront_active_order_uuid
        uuid
      }
    }
  }
`;

export const useOrderContextQueryVariables = (
  orderDomains: OrderDomain[],
  restaurantUUID: string,
  supplierUUID: string,
): NewOrder_OrderContextQueryVariables => {
  const authenticatedUserUUID = useAuth((s) => s.authenticatedUserUUID);

  return useMemo(
    () => ({
      employeeUUID: authenticatedUserUUID,
      includeActiveOrder:
        orderDomains.includes(OrderDomain.Storefront) &&
        Boolean(authenticatedUserUUID),
      orderDomains,
      restaurantUUID,
      supplierUUID,
      unplacedOrderStatuses: UNPLACED_ORDER_STATUSES,
    }),
    [authenticatedUserUUID, orderDomains, restaurantUUID, supplierUUID],
  );
};
